import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import {
  clearRememberedActiveMsalAccount,
  getMsalInstanceForAccount,
  loginWithExternalMicrosoft,
  resolveActiveMsalAccountForPortal,
} from "../../authConfig.js";
import { axiosInstance } from "../axiosInstance.js";
import {
  clearStoredPersona,
  getDesiredPortal,
  getSwitchablePersonas,
  resolvePortalHost,
  writeStoredPersona,
} from "../../portal/portalUtils.js";
import {
  buildImpersonationState,
  clearStoredImpersonation,
  getImpersonationForAccount,
  writeStoredImpersonation,
} from "../../authImpersonation.js";
import { isE2EAuthBypassEnabled } from "../../e2eAuthBypass.js";
import { acquireSessionToken, recoverSessionToken } from "../authToken.jsx";
import { announceSessionIssue, selectedGrowerContextKey, sessionAccountKey } from "./sessionRecovery.js";
import { newAttemptId, reportWorkflow } from "../../observability/telemetry.js";
import { clearPortalSession, getPortalSession, getPortalSessionForAccount, getPortalSessionVersion, subscribePortalSession } from "../../portal/portalSession.js";
import { writeSelectedAggregatorGrower } from "../../portal/aggregatorGrowerContext.js";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  if (isE2EAuthBypassEnabled()) {
    return <E2EAuthProvider>{children}</E2EAuthProvider>;
  }

  return <MsalAuthProvider>{children}</MsalAuthProvider>;
};

function E2EAuthProvider({ children }) {
  const hostPortal = resolvePortalHost().portal;
  const value = useMemo(
    () => ({
      user: {
        account: {
          username: "e2e@example.test",
          name: "E2E User",
          tenantId: "e2e",
          homeAccountId: "e2e",
          localAccountId: "e2e",
        },
        email: "e2e@example.test",
        name: "E2E User",
        picture: null,
        provider: "e2e",
        token: "e2e-token",
      },
      viewer: {
        accountType: hostPortal,
        allowedPortals: ["admin", "partner", "aggregator", "grower"],
        portals: ["admin", "partner", "aggregator", "grower"],
        defaultPortal: hostPortal,
        activePortal: hostPortal,
        workforceRoles: [hostPortal],
        canSwitchPersona: false,
      },
      viewerLoading: false,
      viewerError: null,
      sessionIssue: null,
      hasWorkspace: true,
      recoverSession: async () => true,
      retrySession: async () => true,
      login: async () => {},
      logout: async () => {},
      isAuthenticated: true,
      hostPortal,
      activePortal: hostPortal,
      isPortalSimulated: false,
      personaPortal: null,
      isImpersonating: false,
      impersonatedEmail: null,
      impersonationState: null,
      canImpersonateUsers: false,
      startImpersonation: () => false,
      stopImpersonation: () => {},
      setPersonaPortal: () => {},
      clearPersonaPortalOverride: () => {},
      availablePersonas: [],
    }),
    [hostPortal]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function MsalAuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [tokenState, setTokenState] = useState(null);
  const [viewerState, setViewerState] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [sessionIssue, setSessionIssue] = useState(null);
  const portalSession = useSyncExternalStore(subscribePortalSession, getPortalSession, getPortalSession);
  const lastVerifiedAccount = useRef(null);
  const signingOut = useRef(false);
  const current = useRef({});
  const flights = useRef(new Map());
  const latestAttempt = useRef(0);
  const generation = useRef({ key: null, value: 0 });
  const hostPortal = resolvePortalHost().portal;
  const activeAccount = resolveActiveMsalAccountForPortal(instance, accounts, hostPortal);
  // Keep an established editor available while a missing cache/account is recovered.
  // An explicit logout or a different account never inherits that workspace.
  const effectiveAccount = activeAccount || (!accounts.length && !signingOut.current ? lastVerifiedAccount.current : null);
  const isAuthenticated = msalIsAuthenticated && Boolean(activeAccount);
  const ownedPortalSession = getPortalSessionForAccount(effectiveAccount, portalSession);
  const personaPortal = ownedPortalSession.personaPortal;
  const activeImpersonation = getImpersonationForAccount(effectiveAccount, ownedPortalSession.impersonation);
  const impersonatedEmail = activeImpersonation?.impersonatedEmail || null;
  const selectedGrower = selectedGrowerContextKey(effectiveAccount);
  const simulatedPortal = ownedPortalSession.portal;
  const simulationVersion = getPortalSessionVersion();
  const contextKey = JSON.stringify([sessionAccountKey(effectiveAccount), hostPortal, simulatedPortal, simulationVersion, impersonatedEmail, selectedGrower]);
  if (generation.current.key !== contextKey) {
    generation.current = { key: contextKey, value: generation.current.value + 1 };
  }
  current.current = { instance, account: effectiveAccount, contextKey, simulatedPortal, simulationVersion, selectedGrower, inProgress, isAuthenticated, sessionIssue, generation: generation.current.value };
  const viewer = viewerState?.contextKey === contextKey ? viewerState.value : null;
  const accessToken = isAuthenticated && tokenState?.contextKey === contextKey ? tokenState.value : null;
  const hasWorkspace = Boolean(viewer && effectiveAccount && !signingOut.current);
  const isImpersonating = Boolean(impersonatedEmail);
  const canImpersonateUsers = Boolean(viewer?.canSwitchPersona || activeImpersonation);
  const availablePersonas = useMemo(() => isImpersonating ? [] : getSwitchablePersonas(viewer), [isImpersonating, viewer]);
  const activePortal = isImpersonating ? simulatedPortal : getDesiredPortal(viewer, personaPortal, hostPortal);
  const isPortalSimulated = Boolean(simulatedPortal && (isImpersonating || availablePersonas.includes(personaPortal)));

  const refreshSession = useCallback(({ interactive = false, forceRefresh = false } = {}) => {
    const snapshot = current.current;
    if (!snapshot.account || signingOut.current) return Promise.resolve(false);
    if (!interactive && (!snapshot.isAuthenticated || snapshot.inProgress !== "none")) return Promise.resolve(false);
    const flightKey = `${snapshot.contextKey}:${interactive}`;
    if (!interactive && flights.current.has(`${snapshot.contextKey}:true`)) return flights.current.get(`${snapshot.contextKey}:true`);
    if (flights.current.has(flightKey)) return flights.current.get(flightKey);
    const attemptNumber = ++latestAttempt.current;
    const stillCurrent = () => !signingOut.current && latestAttempt.current === attemptNumber && current.current.contextKey === snapshot.contextKey && current.current.generation === snapshot.generation && selectedGrowerContextKey(snapshot.account) === snapshot.selectedGrower && getPortalSessionVersion() === snapshot.simulationVersion && getPortalSessionForAccount(snapshot.account).portal === snapshot.simulatedPortal;
    const attemptId = newAttemptId();
    const task = (async () => {
      setViewerLoading(true);
      try {
        const response = interactive
          ? await recoverSessionToken(snapshot.instance, snapshot.account)
          : await acquireSessionToken(snapshot.instance, undefined, snapshot.account, forceRefresh);
        if (!stillCurrent()) return false;
        if (!response?.accessToken) throw Object.assign(new Error("Sign in to resume your session."), { errorCode: "no_account_error" });
        const result = await axiosInstance.get("/session/viewer");
        if (!stillCurrent()) return false;
        if (!result?.data) throw new Error("Portal session was unavailable.");
        lastVerifiedAccount.current = snapshot.account;
        setTokenState({ contextKey: snapshot.contextKey, value: response.accessToken });
        setViewerState({ contextKey: snapshot.contextKey, value: result.data });
        setViewerError(null);
        setSessionIssue(null);
        if (interactive || snapshot.sessionIssue) {
          window.dispatchEvent(new Event("fieldprint:session-recovered"));
        }
        if (interactive) {
          reportWorkflow({ workflow: "auth", outcome: "succeeded", reason: "completed", attemptId, auth_stage: "interactive_recovery", auth_lane: hostPortal === "grower" ? "external" : "workforce" });
        }
        return true;
      } catch (error) {
        if (!stillCurrent()) return false;
        if (error?.response?.status === 403 && impersonatedEmail) {
          clearStoredImpersonation();
          setViewerState(null);
          setViewerError(null);
          return false;
        }
        setTokenState(null);
        setViewerError(error);
        setSessionIssue(announceSessionIssue(error, { stage: interactive ? "interactive_recovery" : "viewer_refresh", lane: hostPortal === "grower" ? "external" : "workforce", attemptId }));
        return false;
      } finally {
        if (stillCurrent()) setViewerLoading(false);
        flights.current.delete(flightKey);
      }
    })();
    flights.current.set(flightKey, task);
    return task;
  }, [hostPortal, impersonatedEmail]);

  useEffect(() => {
    if (isAuthenticated && inProgress === "none") refreshSession();
  }, [contextKey, inProgress, isAuthenticated, refreshSession]);

  useEffect(() => {
    if (hasWorkspace && !isAuthenticated && inProgress === "none") {
      setTokenState(null);
      setSessionIssue(announceSessionIssue({ errorCode: "no_account_error" }, { stage: "silent_token", lane: hostPortal === "grower" ? "external" : "workforce" }));
    }
  }, [hasWorkspace, hostPortal, inProgress, isAuthenticated]);

  useEffect(() => {
    const pause = (event) => setSessionIssue(event.detail?.reason || "interaction_required");
    const foreground = () => {
      if (document.visibilityState !== "hidden") refreshSession();
    };
    window.addEventListener("fieldprint:session-expired", pause);
    window.addEventListener("focus", foreground);
    window.addEventListener("online", foreground);
    document.addEventListener("visibilitychange", foreground);
    const interval = window.setInterval(foreground, 5 * 60 * 1000);
    return () => {
      window.removeEventListener("fieldprint:session-expired", pause);
      window.removeEventListener("focus", foreground);
      window.removeEventListener("online", foreground);
      document.removeEventListener("visibilitychange", foreground);
      window.clearInterval(interval);
    };
  }, [refreshSession]);

  useEffect(() => {
    const target = effectiveAccount ? getMsalInstanceForAccount(effectiveAccount) : instance;
    target?.enableAccountStorageEvents?.();
    const callback = target?.addEventCallback?.((event) => {
      if (["msal:logoutStart", "msal:logoutSuccess", "msal:accountRemoved"].includes(event.eventType)) {
        const removedAccount = event.payload?.account || event.payload;
        if (event.eventType === "msal:accountRemoved" && sessionAccountKey(removedAccount) !== sessionAccountKey(current.current.account)) return;
        window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
        signingOut.current = true;
        lastVerifiedAccount.current = null;
        generation.current.value += 1;
        clearPortalSession();
        setViewerState(null);
        setTokenState(null);
      }
    });
    return () => { if (callback) target?.removeEventCallback?.(callback); };
  }, [effectiveAccount, instance]);

  useEffect(() => {
    if (activeAccount) signingOut.current = false;
  }, [activeAccount]);

  useEffect(() => {
    if (portalSession.owner && effectiveAccount && !ownedPortalSession.owner) {
      clearPortalSession();
    }
  }, [effectiveAccount, ownedPortalSession.owner, portalSession]);

  useEffect(() => {
    if (viewer && ((isImpersonating && !viewer.canSwitchPersona) ||
        (personaPortal && !getSwitchablePersonas(viewer).includes(personaPortal)))) {
      clearPortalSession();
    }
  }, [isImpersonating, personaPortal, viewer]);

  const user = useMemo(() => effectiveAccount ? {
    account: effectiveAccount,
    email: effectiveAccount.username || effectiveAccount.idTokenClaims?.preferred_username || null,
    name: effectiveAccount.name || effectiveAccount.username || "User",
    picture: null, provider: "microsoft", token: accessToken,
  } : null, [accessToken, effectiveAccount]);

  const login = useCallback(async () => { await loginWithExternalMicrosoft(); }, []);
  const recoverSession = useCallback(() => refreshSession({ interactive: true }), [refreshSession]);
  const retrySession = useCallback(() => refreshSession({ forceRefresh: true }), [refreshSession]);

  const logout = useCallback(async () => {
    window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
    signingOut.current = true;
    lastVerifiedAccount.current = null;
    generation.current.value += 1;
    clearStoredPersona();
    clearStoredImpersonation();
    clearRememberedActiveMsalAccount();
    setTokenState(null);
    setViewerState(null);
    await getMsalInstanceForAccount(effectiveAccount).logoutPopup({ mainWindowRedirectUri: `${window.location.origin}/login` });
    navigate("/login", { replace: true });
  }, [effectiveAccount, navigate]);

  const setPersonaPortal = useCallback((nextPortal) => {
    if (isImpersonating || !availablePersonas.includes(nextPortal)) return;
    window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
    if (nextPortal === hostPortal) clearStoredPersona();
    else writeStoredPersona(nextPortal, effectiveAccount);
    navigate("/dashboard", { replace: true });
  }, [availablePersonas, effectiveAccount, hostPortal, isImpersonating, navigate]);

  const clearPersonaPortalOverride = useCallback(() => {
    window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
    clearStoredPersona();
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const startImpersonation = useCallback((nextEmail, targetPortal = "grower") => {
    if (!viewer?.canSwitchPersona || isImpersonating) return false;
    const nextState = buildImpersonationState({ impersonatedEmail: nextEmail, account: effectiveAccount });
    if (!nextState) return false;
    window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
    if (!writeStoredImpersonation(nextState, targetPortal)) return false;
    writeSelectedAggregatorGrower(null);
    setViewerState(null);
    setViewerError(null);
    navigate("/dashboard", { replace: true });
    return nextState;
  }, [effectiveAccount, isImpersonating, navigate, viewer]);

  const stopImpersonation = useCallback(() => {
    window.dispatchEvent(new Event("fieldprint:checkpoint-drafts"));
    clearStoredPersona();
    clearStoredImpersonation();
    writeSelectedAggregatorGrower(null);
    setViewerState(null);
    setViewerError(null);
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const value = {
    user, viewer, viewerLoading, viewerError, sessionIssue, hasWorkspace,
    login, logout, recoverSession, retrySession, isAuthenticated, hostPortal, activePortal, isPortalSimulated,
    personaPortal, isImpersonating, impersonatedEmail, impersonationState: activeImpersonation,
    canImpersonateUsers, startImpersonation, stopImpersonation, setPersonaPortal,
    clearPersonaPortalOverride, availablePersonas,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
